import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getFirestore, collection, query, where, getDocs, doc, updateDoc, arrayUnion, getDoc, onSnapshot, limit } from "firebase/firestore";
import app from "../../firebase";

const generateRefNo = () =>
  `REF-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;

export default function EnrollmentFee() {
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [viewStudent, setViewStudent] = useState(null);
  const [paymentOption, setPaymentOption] = useState("CASH PAYMENT WITH DISCOUNT");
  const [amount, setAmount] = useState("");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [printed, setPrinted] = useState(false);
  const [message, setMessage] = useState("");
  const [miscFees, setMiscFees] = useState([]);
  const [cashDiscount, setCashDiscount] = useState(0);
  const [cashWithDiscountDate, setCashWithDiscountDate] = useState("");
  const [cashWithoutDiscountDate, setCashWithoutDiscountDate] = useState("");
  const [installmentDates, setInstallmentDates] = useState([]);
  const [refNo, setRefNo] = useState("");
  const [assessmentSchoolYear, setAssessmentSchoolYear] = useState("");
  const [assessmentSemester, setAssessmentSemester] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [selectedPaymentOption, setSelectedPaymentOption] = useState("OPTION_1");
  const rowsPerPage = 10;
  const feeOptions = [
    "CASH PAYMENT WITH DISCOUNT",
    "CASH PAYMENT WITHOUT DISCOUNT",
    "INSTALLMENT PAYMENT"
  ];
  const navigate = useNavigate();

  useEffect(() => {
    const db = getFirestore(app);
    const q = query(
      collection(db, "students"),
      where("registrationStatus", "in", ["submitted", "enrolled"])
    );
    const unsubscribe = onSnapshot(q, (snap) => {
      setStudents(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (err) => {
      console.error("Error fetching students:", err);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!viewStudent) return;
    const refreshed = students.find(s => s.id === viewStudent.id);
    if (refreshed) setViewStudent(refreshed);
  }, [students, viewStudent]);

  useEffect(() => {
    const fetchFees = async () => {
      const db = getFirestore(app);
      const ref = doc(db, "systemSettings", "miscellaneousFees");
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        setMiscFees(data.fees || []);
        setCashDiscount(data.cashDiscount || 0);
        setCashWithDiscountDate(data.cashWithDiscountDate || "");
        setCashWithoutDiscountDate(data.cashWithoutDiscountDate || "");
        setInstallmentDates(data.installmentDates || []);
      }
    };
    fetchFees();
  }, []);

  const filtered = students.filter(
    s =>
      s.registrationStatus === "enrolled" || s.registrationStatus === "submitted" &&
      (
        s.idNumber?.toLowerCase().includes(search.toLowerCase()) ||
        `${s.lastName}, ${s.firstName}`.toLowerCase().includes(search.toLowerCase())
      )
  );
  const paginated = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const totalPages = Math.ceil(filtered.length / rowsPerPage);

  const units = viewStudent?.totalUnits
    ? parseFloat(viewStudent.totalUnits)
    : Array.isArray(viewStudent?.schedules)
      ? viewStudent.schedules.reduce((sum, c) => sum + (parseFloat(c.units) || 0), 0)
      : 0;

  let total = 0;
  const feeRows = miscFees.map((fee, idx) => {
    let amount = 0;
    let formula = "";
    if (
      fee.formula &&
      fee.formula.toLowerCase().includes("units") &&
      typeof fee.amount === "number"
    ) {
      amount = units * fee.amount;
      formula = `(${units} units X ${fee.amount})`;
    } else if (fee.perUnit !== undefined) {
      amount = units * fee.perUnit;
      formula = `(${units} units X ${fee.perUnit})`;
    } else if (fee.formula) {
      formula = fee.formula;
      amount = fee.amount || 0;
    } else {
      amount = fee.amount || 0;
    }
    total += amount;
    return (
      <tr key={idx}>
        <td>
          {fee.label}
          {formula && (
            <span style={{ color: '#888', fontSize: 13, marginLeft: 6 }}>
              {formula}
            </span>
          )}
        </td>
        <td style={{ textAlign: 'right', borderBottom: '2px solid #bdbdbd', color: '#4b3c0a' }}>
          {amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </td>
      </tr>
    );
  });
  const totalWithDiscount = total - cashDiscount;
  const installment = parseFloat((total / 5).toFixed(2));

  let fee = totalWithDiscount;
  if (paymentOption === "CASH PAYMENT WITHOUT DISCOUNT") fee = total;
  if (paymentOption === "INSTALLMENT PAYMENT") fee = installment;

  const paidAmount = parseFloat(amount) || 0;
  const change = paymentOption === "INSTALLMENT PAYMENT"
    ? (paidAmount > fee ? paidAmount - fee : 0)
    : (paidAmount > fee ? paidAmount - fee : 0);

  let balance = 0;
  const totalInstallment = total;

  if (
    viewStudent &&
    (viewStudent.EnrollmentFeeStatus === 'Paid' || viewStudent.status === 'Fully Paid')
  ) {
    balance = 0;
  } else if (paymentOption === "INSTALLMENT PAYMENT") {
    balance = printed ? totalInstallment - fee : totalInstallment;
  } else {
    balance = fee - paidAmount;
  }
  if (balance < 0) balance = 0;

  const db = getFirestore(app);

  // Helper functions for installments
  const getNextInstallmentStatus = (student) => {
    if (!student.installmentPayments || typeof student.installmentPayments !== 'object' || Object.keys(student.installmentPayments).length === 0) {
      return "Downpayment";
    }
    
    const termKey = `${assessmentSchoolYear}_${assessmentSemester}`;
    const termPayments = student.installmentPayments[termKey];
    
    if (!Array.isArray(termPayments) || termPayments.length === 0) {
      return "Downpayment";
    }
    
    const payments = termPayments[termPayments.length - 1];
    const order = ["Downpayment", "1st Installment", "2nd Installment", "3rd Installment", "4th Installment"];
    for (let i = 0; i < order.length; i++) {
      if (!payments[order[i]]) {
        return order[i];
      }
    }
    if (payments["4th Installment"] && payments["4th Installment"].balance <= 0) {
      return "Fully Paid";
    }
    return "Fully Paid";
  };

  function getNextInstallmentAndBalance(student) {
    if (!student.installmentPayments || typeof student.installmentPayments !== 'object' || Object.keys(student.installmentPayments).length === 0) {
      return { nextLabel: "Downpayment", key: "Downpayment", balance: 0, fee: 0 };
    }
    
    const termKey = `${assessmentSchoolYear}_${assessmentSemester}`;
    const termPayments = student.installmentPayments[termKey];
    
    if (!Array.isArray(termPayments) || termPayments.length === 0) {
      return { nextLabel: "Downpayment", key: "Downpayment", balance: 0, fee: 0 };
    }
    
    const payments = termPayments[termPayments.length - 1];
    const order = ["Downpayment", "1st Installment", "2nd Installment", "3rd Installment", "4th Installment"];
    let lastPaid = null;
    for (let i = 0; i < order.length; i++) {
      if (!payments[order[i]]) {
        let balance = lastPaid && lastPaid.balance !== undefined ? lastPaid.balance : 0;
        let fee = lastPaid && lastPaid.fee !== undefined ? lastPaid.fee : 0;
        return { nextLabel: order[i], key: order[i], balance, fee };
      }
      lastPaid = payments[order[i]];
    }
    return { nextLabel: "Fully Paid", key: null, balance: 0, fee: 0 };
  }

  function getInstallmentRemainingBalance(student, termKey, totalAmount) {
    if (!student || !termKey || !totalAmount) return 0;
    const termPayments = student.installmentPayments?.[termKey];
    if (!Array.isArray(termPayments) || termPayments.length === 0) return totalAmount;
    const payments = termPayments[termPayments.length - 1] || {};
    const order = ["Downpayment", "1st Installment", "2nd Installment", "3rd Installment", "4th Installment"];
    const totalPaid = order.reduce((sum, key) => sum + (parseFloat(payments[key]?.amount) || 0), 0);
    return Math.max(0, totalAmount - totalPaid);
  }

  // Check if student has installments for selected term
  const hasInstallmentsForTerm = () => {
    if (!viewStudent || !assessmentSchoolYear || !assessmentSemester) return false;
    const termKey = `${assessmentSchoolYear}_${assessmentSemester}`;
    const termPayments = viewStudent.installmentPayments?.[termKey];
    return Array.isArray(termPayments) && termPayments.length > 0;
  };

  const handlePrint = async () => {
    const studentRef = doc(db, "students", viewStudent.id);
    
    // Generate Assessment Number (format: YY-MMDD-XXXX where XXXX is random)
    const now = new Date();
    const year = now.getFullYear().toString().slice(-2);
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    const assessmentNo = `${year}-${month}${day}${random}`;

    const termKey = `${assessmentSchoolYear}_${assessmentSemester}`;
    
    // Calculate correct fee from assessment snapshot if available
    let printFee = fee;
    if (assessmentMiscSnapshot) {
      if (paymentOption === "CASH PAYMENT WITH DISCOUNT") {
        printFee = assessmentMiscSnapshot.totalWithDiscount || assessmentMiscSnapshot.totalAmount;
      } else if (paymentOption === "CASH PAYMENT WITHOUT DISCOUNT") {
        printFee = assessmentMiscSnapshot.totalAmount;
      } else if (paymentOption === "INSTALLMENT PAYMENT") {
        printFee = assessmentMiscSnapshot.installmentAmount || parseFloat((assessmentMiscSnapshot.totalAmount / 5).toFixed(2));
      }
    }
    
    const paidAmount = parseFloat(amount) || 0;
    const printChange = paidAmount > printFee ? paidAmount - printFee : 0;
    const netPaid = Math.max(0, paidAmount - printChange);
    const installmentTotal = assessmentMiscSnapshot?.totalAmount ?? total;
    let printBalance = 0;
    if (viewStudent.EnrollmentFeeStatus === 'Paid' || viewStudent.status === 'Fully Paid') {
      printBalance = 0;
    } else if (paymentOption === "INSTALLMENT PAYMENT") {
      printBalance = installmentTotal - netPaid;
    } else {
      printBalance = printFee - netPaid;
    }
    if (printBalance < 0) printBalance = 0;

    if (paymentOption === "INSTALLMENT PAYMENT") {
      const installmentInit = {
        Downpayment: {
          amount: netPaid,
          balance: printBalance,
          change: printChange,
          date: new Date().toISOString(),
          fee: printFee,
          payment: "Cash",
          paymentOption: "INSTALLMENT PAYMENT",
          refNo,
          assessmentNo,
          schoolYear: assessmentSchoolYear,
          semester: assessmentSemester
        },
        "1st Installment": null,
        "2nd Installment": null,
        "3rd Installment": null,
        "4th Installment": null,
        paymentType: "INSTALLMENT PAYMENT",
        status: "Installment",
        assessmentNo: assessmentNo
      };

      const existingInstallments = viewStudent.installmentPayments || {};
      const updatedInstallments = {
        ...existingInstallments,
        [termKey]: [installmentInit]
      };

      await updateDoc(studentRef, {
        installmentPayments: updatedInstallments,
        paymentType: "INSTALLMENT PAYMENT"
      });
    } else {
      const paymentPayload = {
        amount: netPaid,
        balance: printBalance,
        change: printChange,
        date: new Date().toISOString(),
        fee: printFee,
        payment: "Cash",
        paymentOption: paymentOption,
        refNo,
        assessmentNo,
        schoolYear: assessmentSchoolYear,
        semester: assessmentSemester,
        EnrollmentFeeStatus: "Paid",
        status: "Fully Paid"
      };

      const existingPayments = viewStudent.payments || {};
      const termPayments = existingPayments[termKey] || [];
      const updatedPayments = {
        ...existingPayments,
        [termKey]: [...termPayments, paymentPayload]
      };

      await updateDoc(studentRef, {
        payments: updatedPayments
      });
    }

    // Generate and print receipt
    const receiptHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Enrollment Fee Receipt</title>
        <style>
          body { margin: 0; padding: 20px; font-family: "Segoe UI", Arial, sans-serif; }
          .receipt { max-width: 600px; margin: 0 auto; }
          .header { text-align: center; margin-bottom: 40px; border-bottom: 2px solid #333; padding-bottom: 20px; }
          h1 { margin: 0 0 10px 0; font-size: 28px; font-weight: 700; }
          .date { font-size: 12px; color: #666; margin-top: 10px; }
          .details { margin-bottom: 30px; font-size: 16px; line-height: 1.8; }
          .detail-row { display: flex; justify-content: space-between; margin-bottom: 8px; }
          .detail-label { font-weight: 600; }
          .detail-value { text-align: right; }
          .ref-no { color: #155c2b; font-weight: 700; }
          .summary { background-color: #f9f9f9; padding: 20px; border-radius: 6px; margin-bottom: 30px; font-size: 16px; }
          .summary-row { display: flex; justify-content: space-between; margin-bottom: 12px; }
          .summary-label { font-weight: 600; }
          .summary-value { font-weight: 700; text-align: right; }
          .balance-row { display: flex; justify-content: space-between; padding-top: 12px; border-top: 2px solid #ddd; font-size: 18px; }
          .footer { text-align: center; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 20px; }
          .footer p { margin: 5px 0; }
          @media print {
            body { padding: 0; }
          }
        </style>
      </head>
      <body>
        <div class="receipt">
          <div class="header">
            <h1>Enrollment Fee</h1>
            <div class="date">${new Date().toLocaleString()}</div>
          </div>
          
          <div class="details">
            <div class="detail-row">
              <div class="detail-label">Reference No:</div>
              <div class="detail-value ref-no">${refNo}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">Name:</div>
              <div class="detail-value">${viewStudent.lastName}, ${viewStudent.firstName}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">ID Number:</div>
              <div class="detail-value">${viewStudent.idNumber}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">School Year:</div>
              <div class="detail-value">${assessmentSchoolYear}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">Semester:</div>
              <div class="detail-value">${assessmentSemester}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">Assessment No:</div>
              <div class="detail-value">${assessmentNo}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">Payment Option:</div>
              <div class="detail-value">${paymentOption.replace("PAYMENT", "Payment")}</div>
            </div>
          </div>

          <div class="summary">
            <div class="summary-row">
              <div class="summary-label">Fee:</div>
              <div class="summary-value">₱ ${printFee.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
            <div class="summary-row">
              <div class="summary-label">Amount Paid:</div>
              <div class="summary-value">₱ ${netPaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
            <div class="summary-row">
              <div class="summary-label">Change:</div>
              <div class="summary-value">₱ ${printChange.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
            <div class="balance-row">
              <div class="summary-label">Balance:</div>
              <div class="summary-value" style="color: ${printBalance === 0 ? '#2e7d32' : '#d97d2c'}">₱ ${printBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
          </div>

          <div class="footer">
            <p>This is an official receipt. Please keep this for your records.</p>
            <p>Thank you for your payment.</p>
          </div>
        </div>
      </body>
      </html>
    `;

    // Open receipt in new window and print
    const printWindow = window.open('', 'PRINT_RECEIPT', 'width=800,height=600');
    printWindow.document.write(receiptHTML);
    printWindow.document.close();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
      setShowPaymentModal(false);
      setRefNo(generateRefNo());
    }, 250);
  };

  // Handle installment print
  const handleInstallmentPrint = async () => {
    if (!viewStudent) return;
    
    const db = getFirestore(app);
    const studentRef = doc(db, "students", viewStudent.id);
    const termKey = `${assessmentSchoolYear}_${assessmentSemester}`;
    
    const { nextLabel, key, balance, fee } = getNextInstallmentAndBalance(viewStudent);
    const installmentTotal = assessmentMiscSnapshot?.totalAmount ?? total;
    const remainingBalance = getInstallmentRemainingBalance(viewStudent, termKey, installmentTotal);
    const balanceForCalc = remainingBalance > 0 ? remainingBalance : balance;
    if (balanceForCalc <= 0) {
      setMessage("No remaining balance.");
      return;
    }
    const paidAmount = parseFloat(paymentAmount) || 0;
    
    // Calculate based on selected option
    let change = 0;
    let newBalance = 0;
    let isFullyPaid = false;
    
    if (selectedPaymentOption === "OPTION_2") {
      // Pay full balance - calculate based on actual payment
      change = paidAmount > balanceForCalc ? paidAmount - balanceForCalc : 0;
    } else {
      // Pay this installment fee only
      change = paidAmount > fee ? paidAmount - fee : 0;
    }
    const netPaid = Math.max(0, paidAmount - change);
    if (selectedPaymentOption === "OPTION_2") {
      newBalance = Math.max(0, balanceForCalc - netPaid);
      isFullyPaid = netPaid >= balanceForCalc;
    } else {
      newBalance = Math.max(0, balanceForCalc - fee);
      isFullyPaid = newBalance <= 0;
    }

    if (!key) return;

    const existingInstallments = viewStudent.installmentPayments || {};
    const termPayments = existingInstallments[termKey] || [{}];
    const lastPayment = termPayments[termPayments.length - 1];
    
    // Use existing assessment number from Downpayment
    const assessmentNo = lastPayment.Downpayment?.assessmentNo || viewStudent.assessmentNo;
    
    lastPayment[key] = {
      amount: netPaid,
      balance: newBalance,
      change: change,
      date: new Date().toISOString(),
      fee: fee,
      payment: "Cash",
      paymentOption: "INSTALLMENT PAYMENT",
      refNo,
      assessmentNo,
      schoolYear: assessmentSchoolYear,
      semester: assessmentSemester
    };

    let fullyPaid = false;
    if (isFullyPaid) {
      // OPTION 2 was selected - mark as fully paid
      fullyPaid = true;
    } else if (key === "4th Installment" && lastPayment["4th Installment"] && lastPayment["4th Installment"].balance <= 0) {
      // OPTION 1 - check if this was the last installment
      fullyPaid = true;
    }

    // Update status and enrollment fee status in payment object
    lastPayment.status = fullyPaid ? "Fully Paid" : "Installment";
    if (fullyPaid) {
      lastPayment.EnrollmentFeeStatus = "Paid";
    }

    const updatedInstallments = {
      ...existingInstallments,
      [termKey]: termPayments
    };

    // Generate and print receipt
    const receiptHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Installment Fee Receipt</title>
        <style>
          body { margin: 0; padding: 20px; font-family: "Segoe UI", Arial, sans-serif; }
          .receipt { max-width: 600px; margin: 0 auto; }
          .header { text-align: center; margin-bottom: 40px; border-bottom: 2px solid #333; padding-bottom: 20px; }
          h1 { margin: 0 0 10px 0; font-size: 28px; font-weight: 700; }
          .date { font-size: 12px; color: #666; margin-top: 10px; }
          .details { margin-bottom: 30px; font-size: 16px; line-height: 1.8; }
          .detail-row { display: flex; justify-content: space-between; margin-bottom: 8px; }
          .detail-label { font-weight: 600; }
          .detail-value { text-align: right; }
          .ref-no { color: #155c2b; font-weight: 700; }
          .summary { background-color: #f9f9f9; padding: 20px; border-radius: 6px; margin-bottom: 30px; font-size: 16px; }
          .summary-row { display: flex; justify-content: space-between; margin-bottom: 12px; }
          .summary-label { font-weight: 600; }
          .summary-value { font-weight: 700; text-align: right; }
          .balance-row { display: flex; justify-content: space-between; padding-top: 12px; border-top: 2px solid #ddd; font-size: 18px; }
          .footer { text-align: center; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 20px; }
          .footer p { margin: 5px 0; }
          @media print {
            body { padding: 0; }
          }
        </style>
      </head>
      <body>
        <div class="receipt">
          <div class="header">
            <h1>Installment Fee</h1>
            <div class="date">${new Date().toLocaleString()}</div>
          </div>
          
          <div class="details">
            <div class="detail-row">
              <div class="detail-label">Reference No:</div>
              <div class="detail-value ref-no">${refNo}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">Name:</div>
              <div class="detail-value">${viewStudent.lastName}, ${viewStudent.firstName}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">ID Number:</div>
              <div class="detail-value">${viewStudent.idNumber}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">School Year:</div>
              <div class="detail-value">${assessmentSchoolYear}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">Semester:</div>
              <div class="detail-value">${assessmentSemester}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">Assessment No:</div>
              <div class="detail-value">${assessmentNo}</div>
            </div>
            <div class="detail-row">
              <div class="detail-label">Installment Type:</div>
              <div class="detail-value">${nextLabel}</div>
            </div>
          </div>

          <div class="summary">
            <div class="summary-row">
              <div class="summary-label">Installment Fee:</div>
              <div class="summary-value">₱ ${fee.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
            <div class="summary-row">
              <div class="summary-label">Amount Paid:</div>
              <div class="summary-value">₱ ${netPaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
            <div class="summary-row">
              <div class="summary-label">Change:</div>
              <div class="summary-value">₱ ${change.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
            <div class="balance-row">
              <div class="summary-label">Balance:</div>
              <div class="summary-value" style="color: ${newBalance === 0 ? '#2e7d32' : '#d97d2c'}">₱ ${newBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
          </div>

          <div class="footer">
            <p>This is an official receipt. Please keep this for your records.</p>
            <p>Thank you for your payment.</p>
          </div>
        </div>
      </body>
      </html>
    `;

    // Open receipt in new window and print
    const printWindow = window.open('', 'PRINT_RECEIPT', 'width=800,height=600');
    printWindow.document.write(receiptHTML);
    printWindow.document.close();
    
    setTimeout(async () => {
      printWindow.print();
      printWindow.close();
      
      await updateDoc(studentRef, {
        installmentPayments: updatedInstallments,
        paymentType: "INSTALLMENT PAYMENT"
      });

      setMessage(fullyPaid ? "Student is now fully paid!" : "Payment saved!");
      setShowPaymentModal(false);
      setRefNo(generateRefNo());
    }, 250);
  };

  useEffect(() => {
    if (showPaymentModal && viewStudent) {
      setAmount("");
      setPaymentAmount("");
      setRefNo(generateRefNo());
      setSelectedPaymentOption("OPTION_1");
    }
  }, [showPaymentModal, viewStudent, paymentOption]);

  const getTermKeys = (student) => {
    const scheduleKeys = Object.keys(student?.termSchedules || {});
    const miscKeys = Object.keys(student?.miscellaneousFeesHistory || {});
    return Array.from(new Set([...scheduleKeys, ...miscKeys])).filter(Boolean);
  };

  useEffect(() => {
    if (!viewStudent) {
      setAssessmentSchoolYear("");
      setAssessmentSemester("");
      return;
    }
    const termKeys = getTermKeys(viewStudent);
    if (termKeys.length === 0) {
      setAssessmentSchoolYear("");
      setAssessmentSemester("");
      return;
    }
    const [schoolYear, semester] = termKeys[termKeys.length - 1].split("_");
    setAssessmentSchoolYear(schoolYear || "");
    setAssessmentSemester(semester || "");
  }, [viewStudent]);

  const assessmentTermKey = assessmentSchoolYear && assessmentSemester
    ? `${assessmentSchoolYear}_${assessmentSemester}`
    : "";
  const assessmentSchedules = assessmentTermKey
    ? (viewStudent?.termSchedules?.[assessmentTermKey] || [])
    : [];
  const [enrichedAssessmentSchedules, setEnrichedAssessmentSchedules] = useState(assessmentSchedules);
  const assessmentMiscEntries = assessmentTermKey
    ? (viewStudent?.miscellaneousFeesHistory?.[assessmentTermKey] || [])
    : [];
  const assessmentMiscSnapshot = Array.isArray(assessmentMiscEntries) && assessmentMiscEntries.length > 0
    ? assessmentMiscEntries[assessmentMiscEntries.length - 1]
    : null;

  // Enrich assessment schedules with `profName` resolved from `users` or prettified fallback
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const db = getFirestore(app);
        const resolved = [];
        for (const c of assessmentSchedules || []) {
          let profName = null;
          const profVal = c.prof;

          if (typeof profVal === 'string' && profVal.includes('@')) {
            try {
              const q = query(collection(db, 'users'), where('email', '==', String(profVal).trim()), limit(1));
              const snap = await getDocs(q);
              if (!snap.empty) {
                const u = snap.docs[0].data() || {};
                profName = u.name || (u.firstName ? `${u.firstName} ${u.lastName || ''}`.trim() : u.email);
              }
            } catch (err) {}
          }

          if (!profName && (c.professorId || (typeof profVal === 'string' && profVal && !profVal.includes('@')))) {
            const candidateId = c.professorId || String(profVal);
            try {
              const userSnap = await getDoc(doc(db, 'users', String(candidateId)));
              if (userSnap.exists()) {
                const u = userSnap.data() || {};
                profName = u.name || (u.firstName ? `${u.firstName} ${u.lastName || ''}`.trim() : u.email || null);
              }
            } catch (err) {}
          }

          if (!profName && typeof profVal === 'string' && profVal.includes('@')) {
            const local = String(profVal).split('@')[0];
            profName = local.split(/[^A-Za-z0-9]+/).filter(Boolean).map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
          }

          resolved.push({ ...c, profName: profName || profVal });
        }
        if (!cancelled) setEnrichedAssessmentSchedules(resolved);
      } catch (e) {
        if (!cancelled) setEnrichedAssessmentSchedules(assessmentSchedules);
      }
    })();
    return () => { cancelled = true; };
  }, [assessmentSchedules]);

  const isInstallmentMode = hasInstallmentsForTerm();

  return (
    <div style={{ padding: 40, minHeight: "100vh", background: "#f5f5f5" }}>
      <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
        <input
          type="text"
          placeholder="Search by ID or Name"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: 350, padding: 12, borderRadius: 4, border: '1px solid #888', fontSize: 18, background: '#7c857c22', marginRight: 24 }}
        />
      </div>
      <table style={{ width: "100%", background: "#fff", borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.06)", borderCollapse: "collapse", marginBottom: 24 }}>
        <thead>
          <tr style={{ background: "#e8e8e8" }}>
            <th style={{ padding: 12, textAlign: "left" }}>ID Number</th>
            <th style={{ padding: 12, textAlign: "left" }}>Name</th>
            <th style={{ padding: 12, textAlign: "left" }}>Category</th>
            <th style={{ padding: 12, textAlign: "left" }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {paginated.length === 0 ? (
            <tr>
              <td colSpan={4} style={{ textAlign: 'center', padding: 32 }}>No students found.</td>
            </tr>
          ) : paginated.map((student, idx) => (
            <tr key={student.id} style={{ borderBottom: "1px solid #eee" }}>
              <td style={{ padding: 10 }}>
                {student.studentId || student.idNumber || student.senior?.idNumber || 'EXAMPLE'}
              </td>
              <td style={{ padding: 10 }}>
                {student.lastName
                  ? `${student.lastName}, ${student.firstName}`
                  : student.senior
                    ? `${student.senior.lastName || ''}, ${student.senior.firstName || ''}`
                    : 'EXAMPLE'}
              </td>
              <td style={{ padding: 10 }}>
                {student.category || student.senior?.category || 'Enrolled Students'}
              </td>
              <td style={{ padding: 10, position: 'relative' }}>
                <button
                  style={{ background: '#7c857c', border: 'none', color: '#fff', borderRadius: 4, padding: '6px 18px', fontWeight: 600, fontSize: 16, cursor: 'pointer' }}
                  onClick={() => {
                    setViewStudent(student);
                  }}
                >View</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8 }}>
        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ background: 'none', border: 'none', fontSize: 28, color: page === 1 ? '#ccc' : '#18803b', cursor: page === 1 ? 'not-allowed' : 'pointer' }}>&#8592;</button>
        {Array.from({ length: totalPages }, (_, i) => (
          <button
            key={i}
            onClick={() => setPage(i + 1)}
            style={{ background: 'none', border: 'none', fontWeight: page === i + 1 ? 700 : 400, color: page === i + 1 ? '#d97d2c' : '#222', fontSize: 18, cursor: 'pointer', minWidth: 28 }}
          >{i + 1}</button>
        ))}
        <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ background: 'none', border: 'none', fontSize: 28, color: page === totalPages ? '#ccc' : '#18803b', cursor: page === totalPages ? 'not-allowed' : 'pointer' }}>&#8594;</button>
      </div>

      {/* VIEW MODAL */}
      {viewStudent && !showPaymentModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(0,0,0,0.35)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 10,
            padding: '0 0 32px 0',
            minWidth: 900,
            maxWidth: 1100,
            width: '100%',
            boxShadow: '0 2px 16px rgba(0,0,0,0.13)',
            position: 'relative',
            overflow: 'hidden',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '2px solid #222',
              padding: '32px 48px 16px 48px',
              background: '#fff'
            }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 8 }}>Department:</div>
                <div style={{ fontSize: 16, marginBottom: 8 }}>
                  {viewStudent.senior?.program || viewStudent.program || "N/A"}
                </div>
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 8 }}>School Year:</div>
                <select
                  value={assessmentSchoolYear}
                  onChange={e => {
                    setAssessmentSchoolYear(e.target.value);
                    setAssessmentSemester("");
                  }}
                  style={{ padding: 6, borderRadius: 6, border: '1px solid #ccc', minWidth: 160 }}
                >
                  <option value="">Select</option>
                  {Array.from(new Set(getTermKeys(viewStudent).map(k => k.split("_")[0]).filter(Boolean)))
                    .map(sy => (
                      <option key={sy} value={sy}>{sy}</option>
                    ))}
                </select>
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 8 }}>Semester:</div>
                <select
                  value={assessmentSemester}
                  onChange={e => setAssessmentSemester(e.target.value)}
                  style={{ padding: 6, borderRadius: 6, border: '1px solid #ccc', minWidth: 160 }}
                  disabled={!assessmentSchoolYear}
                >
                  <option value="">Select</option>
                  {Array.from(new Set(
                    getTermKeys(viewStudent)
                      .filter(k => k.startsWith(`${assessmentSchoolYear}_`))
                      .map(k => k.split("_")[1])
                      .filter(Boolean)
                  )).map(sem => (
                    <option key={sem} value={sem}>{sem}</option>
                  ))}
                </select>
              </div>
              <button
                style={{
                  background: '#e67e22',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '8px 28px',
                  fontWeight: 700,
                  fontSize: 16,
                  cursor: 'pointer'
                }}
                onClick={() => setViewStudent(null)}
              >Close</button>
            </div>
            {/* Basic Info */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 32,
              padding: '32px 48px 0 48px',
              background: '#fff'
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 22, marginBottom: 10, textAlign: 'left' }}>Basic Information</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px 32px', fontSize: 16 }}>
                  <div>
                    <div style={{ marginBottom: 16 }}><b>Last Name:</b> {viewStudent.lastName || "N/A"}</div>
                    <div style={{ marginBottom: 16 }}><b>First Name:</b> {viewStudent.firstName || "N/A"}</div>
                    <div style={{ marginBottom: 16 }}><b>Middle Name:</b> {viewStudent.middleName || "N/A"}</div>
                    <div><b>Gender:</b> {viewStudent.gender || "N/A"}</div>
                  </div>
                  <div>
                    <div style={{ marginBottom: 16 }}><b>ID:</b> {viewStudent.studentId || viewStudent.idNumber || "N/A"}</div>
                    <div style={{ marginBottom: 16 }}><b>Mobile No.:</b> {viewStudent.mobile || "N/A"}</div>
                    <div><b>Email Address:</b> {viewStudent.email || "N/A"}</div>
                  </div>
                </div>
              </div>
            </div>
            <div style={{ height: 1, background: '#ddd', margin: '24px 48px 0 48px' }} />

            {/* Payment Status */}
            <div style={{ padding: '24px 48px 0 48px' }}>
              {(() => {
                const termKey = assessmentTermKey;
                if (!termKey) {
                  return <div style={{ color: '#888', fontSize: 16, padding: '16px 0' }}>Select a School Year and Semester to view payment status.</div>;
                }

                const termPayments = viewStudent?.payments?.[termKey] || [];
                const termInstallments = viewStudent?.installmentPayments?.[termKey] || [];
                const miscSnapshot = assessmentMiscSnapshot;

                if (!miscSnapshot) {
                  return <div style={{ color: '#888', fontSize: 16, padding: '16px 0' }}>No assessment found for selected term.</div>;
                }

                const totalAmount = miscSnapshot.totalAmount || 0;
                const assessmentNo = termPayments[0]?.assessmentNo || termInstallments[0]?.Downpayment?.assessmentNo || termInstallments[0]?.assessmentNo || "N/A";

                let transactions = [];

                // Determine effective billing amount based on payment type
                let effectiveBillings = totalAmount;
                if (termPayments.length > 0 && termPayments[0].paymentOption === 'CASH PAYMENT WITH DISCOUNT') {
                  effectiveBillings = termPayments[0].fee || totalAmount;
                }
                // Installment billings should reflect the assessed total amount

                // Add assessment row
                transactions.push({
                  date: miscSnapshot.date || new Date().toISOString().split('T')[0],
                  transaction: 'Assessment',
                  refNo: assessmentNo,
                  description: '',
                  billings: effectiveBillings,
                  payments: 0,
                  balance: effectiveBillings
                });

                let runningBalance = effectiveBillings;

                // Process cash payments
                if (termPayments.length > 0) {
                  termPayments.forEach((payment) => {
                    const paymentDate = payment.date ? new Date(payment.date).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\/(\d+)\/(\d+)\/(\d+)/, '$3/$1/$2') : 'N/A';
                    const desc = payment.paymentOption === 'CASH PAYMENT WITH DISCOUNT' 
                      ? 'CASH PAYMENT WITH DISCOUNT'
                      : payment.paymentOption === 'CASH PAYMENT WITHOUT DISCOUNT'
                      ? 'CASH PAYMENT WITHOUT DISCOUNT'
                      : payment.paymentOption || 'CASH PAYMENT';
                    
                    runningBalance -= payment.amount || 0;
                    
                    transactions.push({
                      date: paymentDate,
                      transaction: 'Official Receipt',
                      refNo: payment.refNo || 'N/A',
                      description: desc,
                      billings: 0,
                      payments: payment.amount || 0,
                      balance: runningBalance
                    });
                  });
                }

                // Process installment payments
                if (termInstallments.length > 0) {
                  const installmentData = termInstallments[0];
                  const installmentOrder = ['Downpayment', '1st Installment', '2nd Installment', '3rd Installment', '4th Installment'];
                  
                  installmentOrder.forEach((key) => {
                    if (installmentData[key] && installmentData[key].date) {
                      const payment = installmentData[key];
                      const paymentDate = new Date(payment.date).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\/(\d+)\/(\d+)\/(\d+)/, '$3/$1/$2');
                      const desc = key === 'Downpayment' 
                        ? 'INSTALLMENT PAYMENT - [Installment Type 1]'
                        : `Due ${new Date(payment.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} (${key.replace(' Installment', '')})`;
                      
                      runningBalance -= payment.amount || 0;
                      
                      transactions.push({
                        date: paymentDate,
                        transaction: 'Official Receipt',
                        refNo: payment.refNo || 'N/A',
                        description: desc,
                        billings: 0,
                        payments: payment.amount || 0,
                        balance: runningBalance
                      });
                    }
                  });
                } 

                const totalBillings = transactions.reduce((sum, t) => sum + t.billings, 0);
                const totalPayments = transactions.reduce((sum, t) => sum + t.payments, 0);
                const finalBalance = runningBalance;

                const statusLabel = finalBalance === effectiveBillings ? 'UNPAID' : finalBalance === 0 ? 'PAID' : 'BALANCES';
                const statusBg = finalBalance === effectiveBillings ? '#ffebee' : finalBalance === 0 ? '#e8f5e9' : '#fff3e0';
                const statusColor = finalBalance === effectiveBillings ? '#c62828' : finalBalance === 0 ? '#2e7d32' : '#e65100';
                const isUnpaid = finalBalance === effectiveBillings;

                // Determine payment option
                let paymentOptionDisplay = 'N/A';
                if (termPayments.length > 0) {
                  paymentOptionDisplay = termPayments[0].paymentOption || 'N/A';
                } else if (termInstallments.length > 0) {
                  paymentOptionDisplay = 'INSTALLMENT PAYMENT';
                }

                return (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                      <div style={{ fontWeight: 700, fontSize: 20 }}>Payment Status</div>
                      <div style={{ 
                        padding: '4px 16px', 
                        borderRadius: 4, 
                        fontWeight: 700, 
                        fontSize: 14,
                        background: '#e3f2fd',
                        color: '#024400'
                      }}>
                        {paymentOptionDisplay.toUpperCase()}
                      </div>
                      <div style={{ 
                        padding: '4px 16px', 
                        borderRadius: 4, 
                        fontWeight: 700, 
                        fontSize: 14,
                        background: statusBg,
                        color: statusColor
                      }}>
                        {statusLabel}
                      </div>
                    </div>
                    {!isUnpaid && (
                      <>
                    <div style={{ background: '#e8e8e8', padding: '12px 16px', fontWeight: 700, fontSize: 18, textAlign: 'center', marginBottom: 16, borderRadius: 6 }}>
                      ASSESSMENT #{assessmentNo}
                    </div>
                    <table style={{ width: '100%', fontSize: 14, borderCollapse: 'collapse', marginBottom: 16 }}>
                      <thead>
                        <tr style={{ background: '#f0f0f0', borderBottom: '2px solid #333' }}>
                          <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 700 }}>Date</th>
                          <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 700 }}>Transaction</th>
                          <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 700 }}>Reference No.</th>
                          <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 700 }}>Description</th>
                          <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 700 }}>Billings</th>
                          <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 700 }}>Payments</th>
                          <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 700 }}>Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((t, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #ddd' }}>
                            <td style={{ padding: '8px' }}>{t.date}</td>
                            <td style={{ padding: '8px' }}>{t.transaction}</td>
                            <td style={{ padding: '8px' }}>{t.refNo}</td>
                            <td style={{ padding: '8px' }}>{t.description}</td>
                            <td style={{ padding: '8px', textAlign: 'right' }}>{t.billings > 0 ? t.billings.toLocaleString(undefined, { minimumFractionDigits: 2 }) : ''}</td>
                            <td style={{ padding: '8px', textAlign: 'right' }}>{t.payments > 0 ? t.payments.toLocaleString(undefined, { minimumFractionDigits: 2 }) : ''}</td>
                            <td style={{ padding: '8px', textAlign: 'right' }}>{t.balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                          </tr>
                        ))}
                        <tr style={{ borderTop: '2px solid #333' }}>
                          <td colSpan={4} style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 700 }}>Previous Balance:</td>
                          <td style={{ padding: '10px 8px', textAlign: 'right' }}></td>
                          <td style={{ padding: '10px 8px', textAlign: 'right' }}></td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 700 }}>0.00</td>
                        </tr>
                        <tr style={{ borderTop: '2px solid #333', background: '#f9f9f9' }}>
                          <td colSpan={4} style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 700, fontSize: 16 }}>TOTAL:</td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 700, fontSize: 16 }}>{totalBillings.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 700, fontSize: 16 }}>{totalPayments.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 700, fontSize: 16 }}>{finalBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                        </tr>
                      </tbody>
                    </table>
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
            <div style={{ height: 1, background: '#ddd', margin: '24px 48px 0 48px' }} />
            {/* Course Line-up */}
            <div style={{ padding: '32px 48px 0 48px', background: '#fff' }}>
              <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 18, letterSpacing: 1 }}>Course Line-up</div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 15, marginBottom: 16 }}>
                <thead>
                  <tr style={{ background: "#f7f7f7" }}>
                    <th style={{ padding: 8, border: '1px solid #ddd', textAlign: "center" }}>Subject Code</th>
                    <th style={{ padding: 8, border: '1px solid #ddd', textAlign: "left" }}>Subject Description</th>
                    <th style={{ padding: 8, border: '1px solid #ddd', textAlign: "center" }}>Section</th>
                    <th style={{ padding: 8, border: '1px solid #ddd', textAlign: "center" }}>Day</th>
                    <th style={{ padding: 8, border: '1px solid #ddd', textAlign: "center" }}>Schedule</th>
                    <th style={{ padding: 8, border: '1px solid #ddd', textAlign: "center" }}>Room</th>
                    <th style={{ padding: 8, border: '1px solid #ddd', textAlign: "center" }}>Instructor</th>
                    <th style={{ padding: 8, border: '1px solid #ddd', textAlign: "right" }}>Units</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(enrichedAssessmentSchedules) && enrichedAssessmentSchedules.length > 0 ? (
                    Object.values(
                      enrichedAssessmentSchedules.reduce((acc, course) => {
                        const key = [course.courseId, course.courseName, course.section].join('|');
                        if (!acc[key]) acc[key] = {
                          courseId: course.courseId,
                          courseName: course.courseName,
                          section: course.section,
                          units: course.units,
                          meetings: []
                        };
                          acc[key].meetings.push({
                          day: course.day,
                          time: course.time,
                          room: course.room,
                          prof: course.profName || course.prof
                        });
                        return acc;
                      }, {})
                    ).map((row, idx) => (
                      <tr key={row.courseId + '-' + row.section} style={{ borderBottom: "1px solid #ddd" }}>
                        <td style={{ padding: "8px", verticalAlign: "top", textAlign: "center" }}>{row.courseId}</td>
                        <td style={{ padding: "8px", verticalAlign: "top" }}>{row.courseName}</td>
                        <td style={{ padding: "8px", verticalAlign: "top", textAlign: "center" }}>{row.section}</td>
                        <td style={{ padding: "8px", verticalAlign: "top", textAlign: "center" }}>
                          {row.meetings.map((m, i) => (
                            <div key={i}>{m.day}</div>
                          ))}
                        </td>
                        <td style={{ padding: "8px", verticalAlign: "top", textAlign: "center" }}>
                          {row.meetings.map((m, i) => (
                            <div key={i}>{m.time}</div>
                          ))}
                        </td>
                        <td style={{ padding: "8px", verticalAlign: "top", textAlign: "center" }}>
                          {row.meetings.map((m, i) => (
                            <div key={i}>{m.room}</div>
                          ))}
                        </td>
                        <td style={{ padding: "8px", verticalAlign: "top", textAlign: "center" }}>
                          {row.meetings.map((m, i) => (
                            <div key={i}>{m.prof}</div>
                          ))}
                        </td>
                        <td style={{ padding: "8px", verticalAlign: "top", textAlign: "right" }}>{row.units}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} style={{ color: "#888", fontSize: 16, textAlign: "center", padding: "24px 0" }}>No course lineup available.</td>
                    </tr>
                  )}
                  <tr>
                    <td colSpan={7} style={{ border: '1px solid #ddd', padding: 8, fontWeight: 'bold', textAlign: 'right' }}>TOTAL UNITS</td>
                    <td style={{ border: '1px solid #ddd', padding: 8, fontWeight: 'bold', textAlign: 'right' }}>
                      {Array.isArray(enrichedAssessmentSchedules)
                        ? Object.values(
                            enrichedAssessmentSchedules.reduce((acc, course) => {
                              const key = [course.courseId, course.courseName, course.section].join('|');
                              if (!acc[key]) acc[key] = { units: parseFloat(course.units) || 0 };
                              return acc;
                            }, {})
                          ).reduce((sum, row) => sum + (parseFloat(row.units) || 0), 0).toFixed(1)
                        : "0"
                      }
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            {/* Miscellaneous Fees */}
            <div style={{ padding: '0 48px 0 48px', background: '#fff' }}>
              {(() => {
                if (!assessmentMiscSnapshot) {
                  return (
                    <div style={{ color: '#888', fontSize: 16, textAlign: 'center', padding: '24px 0' }}>
                      No miscellaneous fees assessment available for the selected term.
                    </div>
                  );
                }

                const units = typeof assessmentMiscSnapshot.totalUnits === 'number'
                  ? assessmentMiscSnapshot.totalUnits
                  : 0;
                let total = 0;
                const feeRows = (assessmentMiscSnapshot.fees || []).map((fee, idx) => {
                  let amount = 0;
                  let formula = "";
                  if (
                    fee.formula &&
                    fee.formula.toLowerCase().includes("units") &&
                    typeof fee.amount === "number"
                  ) {
                    amount = units * fee.amount;
                    formula = `(${units} units X ${fee.amount})`;
                  } else if (fee.perUnit !== undefined) {
                    amount = units * fee.perUnit;
                    formula = `(${units} units X ${fee.perUnit})`;
                  } else if (fee.formula) {
                    formula = fee.formula;
                    amount = fee.amount || 0;
                  } else {
                    amount = fee.amount || 0;
                  }
                  total += amount;
                  return (
                    <tr key={idx}>
                      <td>
                        {fee.label}
                        {formula && (
                          <span style={{ color: '#888', fontSize: 13, marginLeft: 6 }}>
                            {formula}
                          </span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right', borderBottom: '2px solid #bdbdbd', color: '#4b3c0a' }}>
                        {amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  );
                });
                const totalAmount = assessmentMiscSnapshot.totalAmount ?? total;
                const totalWithDiscount = assessmentMiscSnapshot.totalWithDiscount ?? (totalAmount - (assessmentMiscSnapshot.cashDiscount || 0));
                const installment = assessmentMiscSnapshot.installmentAmount ?? parseFloat((totalAmount / 5).toFixed(2));

                return (
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 18, letterSpacing: 1 }}>MISCELLANEOUS FEES</div>
                    <table style={{ width: '100%', fontSize: 16, marginBottom: 18 }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #222' }}>
                          <th style={{ textAlign: 'left', fontWeight: 500 }}> </th>
                          <th style={{ textAlign: 'right', fontWeight: 500 }}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {feeRows}
                        <tr>
                          <td style={{ fontWeight: 700, fontSize: 17, paddingTop: 10 }}>Total:</td>
                          <td style={{ textAlign: 'right', fontWeight: 700, fontSize: 17, paddingTop: 10 }}>
                            {totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <div style={{ fontWeight: 700, color: '#18803b', fontSize: 17, margin: '30px 0 8px 0' }}>
                      OPTION 1: CASH PAYMENT WITH DISCOUNT <span style={{ float: 'right' }}>{totalWithDiscount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div style={{ color: '#222', fontSize: 15, marginBottom: 18 }}>
                      If full payment is made on or before {assessmentMiscSnapshot.cashWithDiscountDate ? new Date(assessmentMiscSnapshot.cashWithDiscountDate).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "N/A"}
                    </div>
                    <div style={{ fontWeight: 700, color: '#18803b', fontSize: 17, margin: '30px 0 8px 0' }}>
                      OPTION 2: CASH PAYMENT WITHOUT DISCOUNT <span style={{ float: 'right' }}>{totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div style={{ color: '#222', fontSize: 15, marginBottom: 18 }}>
                      If full payment is made after {assessmentMiscSnapshot.cashWithoutDiscountDate ? new Date(assessmentMiscSnapshot.cashWithoutDiscountDate).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "N/A"}
                    </div>
                    <div style={{ fontWeight: 700, color: '#18803b', fontSize: 17, margin: '30px 0 8px 0' }}>
                      OPTION 3: INSTALLMENT PAYMENT
                    </div>
                    <table style={{ width: '100%', fontSize: 16, marginBottom: 18 }}>
                      <tbody>
                        {(assessmentMiscSnapshot.installmentDates || []).map((date, idx) => (
                          <tr key={idx}>
                            <td>{["Downpayment", "1st Installment", "2nd Installment", "3rd Installment", "4th Installment"][idx]}</td>
                            <td>{date ? new Date(date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "N/A"}</td>
                            <td style={{ textAlign: 'right' }}>{installment.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                          </tr>
                        ))}
                        <tr>
                          <td style={{ fontWeight: 700 }}>Total</td>
                          <td></td>
                          <td style={{ textAlign: 'right', fontWeight: 700 }}>{totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '32px 48px 0 48px' }}>
              {(() => {
                // Calculate balance to determine if payment button should be disabled
                const termKey = assessmentTermKey;
                let isFullyPaid = false;
                
                if (termKey && viewStudent) {
                  const termPayments = viewStudent?.payments?.[termKey] || [];
                  const termInstallments = viewStudent?.installmentPayments?.[termKey] || [];
                  const miscSnapshot = assessmentMiscSnapshot;
                  
                  if (miscSnapshot) {
                    const totalAmount = miscSnapshot.totalAmount || 0;
                    let effectiveBillings = totalAmount;
                    
                    if (termPayments.length > 0 && termPayments[0].paymentOption === 'CASH PAYMENT WITH DISCOUNT') {
                      effectiveBillings = termPayments[0].fee || totalAmount;
                    }
                    
                    let runningBalance = effectiveBillings;
                    
                    // Subtract cash payments
                    if (termPayments.length > 0) {
                      termPayments.forEach((payment) => {
                        runningBalance -= payment.amount || 0;
                      });
                    }
                    
                    // Subtract installment payments
                    if (termInstallments.length > 0) {
                      const installmentData = termInstallments[0];
                      const installmentOrder = ['Downpayment', '1st Installment', '2nd Installment', '3rd Installment', '4th Installment'];
                      
                      installmentOrder.forEach((key) => {
                        if (installmentData[key] && installmentData[key].date) {
                          runningBalance -= installmentData[key].amount || 0;
                        }
                      });
                    }
                    
                    isFullyPaid = runningBalance === 0;
                  }
                }
                
                return (
                  <button
                    style={{
                      background: isFullyPaid ? '#999' : '#e67e22',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 6,
                      padding: '12px 38px',
                      fontWeight: 700,
                      fontSize: 18,
                      letterSpacing: 1,
                      boxShadow: isFullyPaid ? 'none' : '0 2px 8px rgba(39,174,96,0.07)',
                      cursor: isFullyPaid ? 'not-allowed' : 'pointer',
                      opacity: isFullyPaid ? 0.6 : 1
                    }}
                    onClick={() => {
                      if (!isFullyPaid) {
                        setShowPaymentModal(true);
                      }
                    }}
                    disabled={isFullyPaid}
                  >
                    Payment
                  </button>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* PAYMENT MODAL - Switches between enrollment and installment mode */}
      {showPaymentModal && viewStudent && (() => {
        // Installment mode
        if (isInstallmentMode) {
          const { nextLabel, key, balance, fee } = getNextInstallmentAndBalance(viewStudent);
          const installmentTotal = assessmentMiscSnapshot?.totalAmount ?? total;
          const remainingBalance = getInstallmentRemainingBalance(viewStudent, assessmentTermKey, installmentTotal);
          const balanceForCalc = remainingBalance > 0 ? remainingBalance : balance;
          const noRemainingBalance = balanceForCalc <= 0;
          const paidAmount = parseFloat(paymentAmount) || 0;
          const change = noRemainingBalance ? 0 : (paidAmount > balanceForCalc ? paidAmount - balanceForCalc : 0);
          const newBalance = Math.max(0, balanceForCalc - paidAmount);

          return (
            <div style={{
              position: 'fixed',
              top: 0, left: 0, width: '100vw', height: '100vh',
              background: 'rgba(0,0,0,0.35)', zIndex: 1000,
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <div style={{
                background: "#fff",
                borderRadius: 16,
                padding: "48px 64px 48px 64px",
                minWidth: 600,
                maxWidth: 700,
                width: "100%",
                maxHeight: "90vh",
                boxShadow: "0 2px 24px rgba(0,0,0,0.13)",
                position: "relative",
                overflowY: "auto"
              }}>
                <div style={{ fontWeight: 700, fontSize: 32, textAlign: "center", marginBottom: 40 }}>Instalment Fee</div>
                <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: "24px 18px", fontSize: 20, marginBottom: 32 }}>
                  <div style={{ fontWeight: 600, color: "#555" }}>Reference No:</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#155c2b" }}>{refNo}</div>
                  <div>Name:</div>
                  <div>{viewStudent.lastName}, {viewStudent.firstName}</div>
                  <div>ID Number:</div>
                  <div>{viewStudent.idNumber}</div>
                  <div>School Year:</div>
                  <div>{assessmentSchoolYear || "N/A"}</div>
                  <div>Semester:</div>
                  <div>{assessmentSemester || "N/A"}</div>
                  <div>Payment Option:</div>
                  <div>{nextLabel}</div>
                  <div>Fee:</div>
                  <div>{fee.toLocaleString(undefined, { minimumFractionDigits: 2 })} peso</div>
                  <div>Payment:</div>
                  <div>Cash</div>
                  
                  <div style={{ fontWeight: 700, fontSize: 18, marginTop: 12, gridColumn: '1 / -1', color: '#155c2b', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <input 
                      type="radio" 
                      name="paymentOption" 
                      value="OPTION_1"
                      checked={selectedPaymentOption === "OPTION_1"}
                      onChange={() => setSelectedPaymentOption("OPTION_1")}
                      style={{ cursor: 'pointer' }}
                    />
                    OPTION 1: Pay This Installment
                  </div>
                  <div>Amount:</div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      type="number"
                      min="0"
                      value={paymentAmount}
                      onChange={e => setPaymentAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                      disabled={selectedPaymentOption !== "OPTION_1"}
                      style={{ fontSize: 18, padding: "6px 18px", borderRadius: 10, border: "1px solid #bbb", background: selectedPaymentOption !== "OPTION_1" ? "#e8e8e8" : "#f3f3f3", flex: 1 }}
                    />
                  </div>
                  <div>Change:</div>
                  <div>{(() => {
                    if (selectedPaymentOption !== "OPTION_1") return "0.00";
                    const paidAmt = parseFloat(paymentAmount) || 0;
                    const changeAmt = paidAmt > fee ? paidAmt - fee : 0;
                    return changeAmt.toLocaleString(undefined, { minimumFractionDigits: 2 });
                  })()} peso</div>
                  <div>Balance After Payment:</div>
                  <div>{(() => {
                    if (noRemainingBalance) return "0.00";
                    if (selectedPaymentOption !== "OPTION_1") return "0.00";
                    const balAfter = Math.max(0, balanceForCalc - fee);
                    return balAfter.toLocaleString(undefined, { minimumFractionDigits: 2 });
                  })()} peso</div>

                  <div style={{ fontWeight: 700, fontSize: 18, marginTop: 18, gridColumn: '1 / -1', color: '#155c2b', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <input 
                      type="radio" 
                      name="paymentOption" 
                      value="OPTION_2"
                      checked={selectedPaymentOption === "OPTION_2"}
                      onChange={() => setSelectedPaymentOption("OPTION_2")}
                      style={{ cursor: 'pointer' }}
                    />
                    OPTION 2: Pay Full Balance
                  </div>
                  <div>Amount:</div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      type="number"
                      min="0"
                      value={paymentAmount}
                      onChange={e => setPaymentAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                      disabled={selectedPaymentOption !== "OPTION_2"}
                      placeholder={balanceForCalc.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      style={{ fontSize: 18, padding: "6px 18px", borderRadius: 10, border: "1px solid #bbb", background: selectedPaymentOption !== "OPTION_2" ? "#e8e8e8" : "#f3f3f3", flex: 1 }}
                    />
                  </div>
                  <div>Change:</div>
                  <div>{(() => {
                    if (selectedPaymentOption !== "OPTION_2" || noRemainingBalance) return "0.00";
                    const paidAmt = parseFloat(paymentAmount) || 0;
                    const changeAmt = paidAmt > balanceForCalc ? paidAmt - balanceForCalc : 0;
                    return changeAmt.toLocaleString(undefined, { minimumFractionDigits: 2 });
                  })()} peso</div>
                  <div>Balance After Payment:</div>
                  <div>{(() => {
                    if (selectedPaymentOption !== "OPTION_2" || noRemainingBalance) return "0.00";
                    const paidAmt = parseFloat(paymentAmount) || 0;
                    const balanceAfterPayment = Math.max(0, balanceForCalc - paidAmt);
                    return balanceAfterPayment.toLocaleString(undefined, { minimumFractionDigits: 2 });
                  })()} peso</div>
                </div>
                <div style={{ display: "flex", justifyContent: "center", gap: 32, marginTop: 32 }}>
                  <button
                    style={{ background: "#e0e0e0", color: "#18803b", border: "none", borderRadius: 8, padding: "16px 48px", fontWeight: 700, fontSize: 20, cursor: noRemainingBalance ? "not-allowed" : "pointer", opacity: noRemainingBalance ? 0.6 : 1 }}
                    onClick={handleInstallmentPrint}
                    disabled={noRemainingBalance}
                  >Print</button>
                  <button
                    style={{ background: "#e67e22", color: "#fff", border: "none", borderRadius: 8, padding: "16px 48px", fontWeight: 700, fontSize: 20, cursor: "pointer" }}
                    onClick={() => setShowPaymentModal(false)}
                  >CLOSE</button>
                </div>
                {message && <div style={{ textAlign: "center", color: "#18803b", fontWeight: 700, marginTop: 18 }}>{message}</div>}
              </div>
            </div>
          );
        }

        // Enrollment mode (cash payments)
        return (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(0,0,0,0.35)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: "#fff",
            borderRadius: 16,
            padding: "48px 64px 48px 64px",
            minWidth: 600,
            maxWidth: 700,
            width: "100%",
            maxHeight: "90vh",
            boxShadow: "0 2px 24px rgba(0,0,0,0.13)",
            position: "relative",
            overflowY: "auto"
          }}>
            <div style={{ fontWeight: 700, fontSize: 32, textAlign: "center", marginBottom: 40 }}>Enrollment Fee</div>
            <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: "24px 18px", fontSize: 20, marginBottom: 32 }}>
              <div style={{ fontWeight: 600, color: "#555" }}>Reference No:</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#155c2b" }}>{refNo}</div>
              <div>Name:</div>
              <div>{viewStudent.lastName}, {viewStudent.firstName}</div>
              <div>ID Number:</div>
              <div>{viewStudent.idNumber}</div>
              <div>School Year:</div>
              <div>{assessmentSchoolYear || "N/A"}</div>
              <div>Semester:</div>
              <div>{assessmentSemester || "N/A"}</div>
              <div>Payment Option:</div>
              <div>
                <select
                  value={paymentOption}
                  onChange={e => setPaymentOption(e.target.value)}
                  style={{
                    fontSize: 18,
                    padding: "6px 18px",
                    borderRadius: 10,
                    border: "1px solid #bbb",
                    background: "#f3f3f3"
                  }}
                >
                  {feeOptions.map(opt => (
                    <option key={opt} value={opt}>{opt.replace("PAYMENT", "Payment")}</option>
                  ))}
                </select>
              </div>
              <div>Fee:</div>
              <div>
                {(() => {
                  let displayFee = fee;
                  if (assessmentMiscSnapshot) {
                    if (paymentOption === "CASH PAYMENT WITH DISCOUNT") {
                      displayFee = assessmentMiscSnapshot.totalWithDiscount || assessmentMiscSnapshot.totalAmount;
                    } else if (paymentOption === "CASH PAYMENT WITHOUT DISCOUNT") {
                      displayFee = assessmentMiscSnapshot.totalAmount;
                    } else if (paymentOption === "INSTALLMENT PAYMENT") {
                      displayFee = assessmentMiscSnapshot.installmentAmount || parseFloat((assessmentMiscSnapshot.totalAmount / 5).toFixed(2));
                    }
                  }
                  return displayFee.toLocaleString(undefined, { minimumFractionDigits: 2 });
                })()} Peso
              </div>
              <div>Payment:</div>
              <div>Cash</div>
              <div>Amount:</div>
              <div>
                <input
                  type="number"
                  min="0"
                  value={amount}
                  onChange={e => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                  style={{
                    fontSize: 18,
                    padding: "6px 18px",
                    borderRadius: 10,
                    border: "1px solid #bbb",
                    background: "#f3f3f3",
                    width: "100%"
                  }}
                  disabled={viewStudent.EnrollmentFeeStatus === 'Paid' || viewStudent.status === 'Fully Paid'}
                />
              </div>
              <div>Change:</div>
              <div>
                {(() => {
                  let displayFee = fee;
                  if (assessmentMiscSnapshot) {
                    if (paymentOption === "CASH PAYMENT WITH DISCOUNT") {
                      displayFee = assessmentMiscSnapshot.totalWithDiscount || assessmentMiscSnapshot.totalAmount;
                    } else if (paymentOption === "CASH PAYMENT WITHOUT DISCOUNT") {
                      displayFee = assessmentMiscSnapshot.totalAmount;
                    } else if (paymentOption === "INSTALLMENT PAYMENT") {
                      displayFee = assessmentMiscSnapshot.installmentAmount || parseFloat((assessmentMiscSnapshot.totalAmount / 5).toFixed(2));
                    }
                  }
                  const paidAmount = parseFloat(amount) || 0;
                  const displayChange = paidAmount > displayFee ? paidAmount - displayFee : 0;
                  return displayChange.toLocaleString(undefined, { minimumFractionDigits: 2 });
                })()} Peso
              </div>
              <div>Balance:</div>
              <div>
                {(() => {
                  let displayFee = fee;
                  if (assessmentMiscSnapshot) {
                    if (paymentOption === "CASH PAYMENT WITH DISCOUNT") {
                      displayFee = assessmentMiscSnapshot.totalWithDiscount || assessmentMiscSnapshot.totalAmount;
                    } else if (paymentOption === "CASH PAYMENT WITHOUT DISCOUNT") {
                      displayFee = assessmentMiscSnapshot.totalAmount;
                    } else if (paymentOption === "INSTALLMENT PAYMENT") {
                      displayFee = assessmentMiscSnapshot.installmentAmount || parseFloat((assessmentMiscSnapshot.totalAmount / 5).toFixed(2));
                    }
                  }
                  const paidAmount = parseFloat(amount) || 0;
                  let displayBalance = 0;
                  if (viewStudent.EnrollmentFeeStatus === 'Paid' || viewStudent.status === 'Fully Paid') {
                    displayBalance = 0;
                  } else if (paymentOption === "INSTALLMENT PAYMENT") {
                    const displayTotal = assessmentMiscSnapshot?.totalAmount ?? total;
                    const displayChange = paidAmount > displayFee ? paidAmount - displayFee : 0;
                    const netPaid = Math.max(0, paidAmount - displayChange);
                    displayBalance = displayTotal - netPaid;
                  } else {
                    displayBalance = displayFee - paidAmount;
                  }
                  if (displayBalance < 0) displayBalance = 0;
                  return displayBalance.toLocaleString(undefined, { minimumFractionDigits: 2 });
                })()} Peso
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "center", gap: 32, marginTop: 32 }}>
              <button
                style={{
                  background: "#e0e0e0",
                  color: "#18803b",
                  border: "none",
                  borderRadius: 8,
                  padding: "16px 48px",
                  fontWeight: 700,
                  fontSize: 20,
                  cursor: "pointer"
                }}
                onClick={handlePrint}
                disabled={viewStudent.EnrollmentFeeStatus === 'Paid' || viewStudent.status === 'Fully Paid'}
              >
                Print
              </button>
              <button
                style={{
                  background: "#e67e22",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "16px 48px",
                  fontWeight: 700,
                  fontSize: 20,
                  cursor: "pointer"
                }}
                onClick={() => setShowPaymentModal(false)}
              >
                CLOSE
              </button>
            </div>
            {message && (
              <div style={{ textAlign: "center", color: "#18803b", fontWeight: 700, marginTop: 18 }}>
                {message}
              </div>
            )}
            {(viewStudent.EnrollmentFeeStatus === 'Paid' || viewStudent.status === 'Fully Paid') && (
              <div style={{ textAlign: "center", color: "#d97d2c", fontWeight: 700, marginTop: 18 }}>
                This student is already fully paid.
              </div>
            )}
          </div>
        </div>
        );
      })()}
      </div>
    </div>
  );
}
